import { AttentionBadges } from "@/components/attention-badges";
import { ColumnPresenceFilterChoices } from "@/components/non_registrant/column-presence-filter";
import {
  displayValueMatchesFilters,
  encodeContainsFilterValue,
  withoutPresenceFilterValues,
} from "@/components/non_registrant/dashboard-column-filter-match";
import type { ColumnProperties } from "@/components/non_registrant/dashboard-table";
import {
  LicenseRowActions,
  licenseRowActionsColumnWidth,
} from "@/components/registrant/dashboard/license-row-actions";
import {
  PerformInspectionButton,
  canPerformInspection,
} from "@/components/registrant/dashboard/perform-inspection-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { UserType } from "@/constants";
import type {
  RegistrantLicenseListLicenseResponseSchema,
  RegistrantLicenseListQuestionColumnSchema,
} from "@/hera-api/client/types.gen";
import { attentionColumnParts } from "@/lib/attention-column";
import { listableRegistrantQuestionColumns } from "@/lib/listable-answer-types";
import {
  cn,
  formatCurrency,
  formatDateNoTimezone,
  formatDateTime,
  formatPropertyAddressLines,
  getInspectionStatusDisplay,
  getLicenseActionLink,
  getRegistrationStatusDisplay,
  isCancelableForRegistrant,
  isDeregisterable,
  isDeregisteredCancellationReason,
  isPayable,
  isRejected,
  isRenewable,
  shouldShowInspectionStatusOnLicenseList,
} from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import {
  DollarSign,
  Filter,
  LogOut,
  RefreshCw,
  RotateCcw,
  Trash2,
  X,
} from "lucide-react";
import * as React from "react";

import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { FilterSearchInput } from "@/components/ui/filter-search-input";
import { ListLoadingOverlay } from "@/components/ui/list-loading-overlay";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useRegistrantOrgPermissions } from "@/hooks/use-registrant-org-permissions";

/** Formatted amount due for registrant list rows (API `due_this_period`). */
export function formatRegistrantLicenseAmountDue(
  license: Pick<RegistrantLicenseListLicenseResponseSchema, "due_this_period">,
): string {
  return formatCurrency(license.due_this_period ?? null);
}

type RegistrantTotalDueSource = Pick<
  RegistrantLicenseListLicenseResponseSchema,
  "due_this_period" | "total_due"
>;

/** Formatted all-time balance due for registrant list rows (API `total_due`). */
export function formatRegistrantLicenseTotalDue(
  license: RegistrantTotalDueSource,
): string {
  return formatCurrency(license.total_due ?? license.due_this_period ?? null);
}

function licenseShowsServiceTransferDate(
  license: Pick<
    RegistrantLicenseListLicenseResponseSchema,
    "transferred_from_license_id" | "transfer_in_date" | "effective_date"
  >,
): boolean {
  if (!license.transfer_in_date) {
    return false;
  }
  if (license.transferred_from_license_id) {
    return true;
  }
  return license.transfer_in_date !== license.effective_date;
}

/** Column keys for registrant license list (one visibility state for all jurisdiction boxes). */
export const REGISTRANT_LICENSE_COLUMN_KEYS = [
  "display_id",
  "address",
  "property_apn",
  "county",
  "state",
  "license_type",
  "registrant",
  "status",
  "needs_attention",
  "amount_due",
  "total_due",
  "effective_date",
  "renewal_date",
  "application_date",
  "approval_date",
] as const;

export type RegistrantLicenseColumnKey =
  (typeof REGISTRANT_LICENSE_COLUMN_KEYS)[number];

/** Registrant list columns that always have a value, so Blank / Not blank stay hidden. */
export const REGISTRANT_COLUMNS_WITHOUT_BLANK_FILTER = new Set<string>([
  "display_id",
  "jurisdiction_name",
  "license_type",
  "license_type_name",
  "status",
  "needs_attention",
  "amount_due",
  "total_due",
  "effective_date",
  "application_date",
]);

export function registrantColumnAllowsBlankFilter(columnId: string): boolean {
  return !REGISTRANT_COLUMNS_WITHOUT_BLANK_FILTER.has(columnId);
}

/** Accessor prefix for optional question-backed columns (operator-style). */
export const REGISTRANT_QUESTION_COLUMN_PREFIX = "qcol:";

export function registrantQuestionColumnAccessorKey(columnKey: string): string {
  return `${REGISTRANT_QUESTION_COLUMN_PREFIX}${columnKey}`;
}

const COLUMN_DISPLAY_NAMES: Record<RegistrantLicenseColumnKey, string> = {
  display_id: "ID #",
  address: "Address",
  property_apn: "Property APN",
  county: "County",
  state: "State",
  license_type: "License Type",
  registrant: "Registrant",
  status: "Status",
  needs_attention: "Needs Attention",
  amount_due: "Amount Due",
  total_due: "Total Due",
  effective_date: "Start Date",
  renewal_date: "Renewal Date",
  application_date: "Application Date",
  approval_date: "Approval Date",
};

export function getRegistrantLicenseColumnDisplayName(
  key: RegistrantLicenseColumnKey,
): string {
  return COLUMN_DISPLAY_NAMES[key] ?? key;
}

/** Name and company for the list Registrant column. */
export function getRegistrantColumnDisplay(
  license: Pick<
    RegistrantLicenseListLicenseResponseSchema,
    "registrant_display_name" | "registrant_company_name"
  >,
): { name: string; company: string | null } {
  const name = license.registrant_display_name?.trim() || "—";
  const company = license.registrant_company_name?.trim() || "";
  return { name, company: company || null };
}

function formatRegistrantColumnFilterValue(
  license: Pick<
    RegistrantLicenseListLicenseResponseSchema,
    "registrant_display_name" | "registrant_company_name"
  >,
): string {
  const { name, company } = getRegistrantColumnDisplay(license);
  if (name === "—" && !company) return "";
  if (company && company !== name) return `${name} · ${company}`;
  return name === "—" ? "" : name;
}

function RegistrantColumnLines({
  license,
}: {
  license: Pick<
    RegistrantLicenseListLicenseResponseSchema,
    "registrant_display_name" | "registrant_company_name"
  >;
}) {
  const { name, company } = getRegistrantColumnDisplay(license);
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span>{name}</span>
      {company ? (
        <span className="text-xs text-muted-foreground">{company}</span>
      ) : null}
    </div>
  );
}

export const DEFAULT_REGISTRANT_LICENSE_COLUMN_VISIBILITY: Record<
  RegistrantLicenseColumnKey,
  boolean
> = {
  display_id: true,
  address: true,
  property_apn: false,
  county: true,
  state: true,
  license_type: true,
  registrant: true,
  status: true,
  needs_attention: true,
  amount_due: true,
  total_due: true,
  effective_date: true,
  renewal_date: true,
  application_date: false,
  approval_date: false,
};

function isColumnVisible(
  columnVisibility: Record<string, boolean> | undefined,
  key: string,
  defaultVisible = false,
): boolean {
  if (columnVisibility && key in columnVisibility) {
    return columnVisibility[key] === true;
  }
  if (key.startsWith(REGISTRANT_QUESTION_COLUMN_PREFIX)) {
    return defaultVisible;
  }
  return true;
}

/** Get cell value for filtering (display string; empty becomes "(Empty)"). */
export function getRegistrantLicenseCellValue(
  license: RegistrantLicenseListLicenseResponseSchema,
  columnId: string,
): string {
  if (columnId.startsWith(REGISTRANT_QUESTION_COLUMN_PREFIX)) {
    const columnKey = columnId.slice(REGISTRANT_QUESTION_COLUMN_PREFIX.length);
    const raw = license.question_column_values?.[columnKey] ?? "";
    const s = typeof raw === "string" ? raw : String(raw ?? "");
    return !s.trim() ? "(Empty)" : s;
  }
  // Bulk registrations table (> threshold) uses these accessor ids; card list uses shorter ids.
  if (columnId === "jurisdiction_name") {
    const raw = license.jurisdiction?.name ?? "";
    const s = typeof raw === "string" ? raw : String(raw ?? "");
    return !s.trim() ? "(Empty)" : s;
  }
  if (columnId === "county") {
    const raw = license.jurisdiction?.county ?? "";
    const s = typeof raw === "string" ? raw : String(raw ?? "");
    return !s.trim() ? "(Empty)" : s;
  }
  if (columnId === "state") {
    const raw = license.jurisdiction?.state ?? "";
    const s = typeof raw === "string" ? raw : String(raw ?? "");
    return !s.trim() ? "(Empty)" : s;
  }
  if (columnId === "property_apn") {
    const raw = license.property?.apn ?? "";
    const s = typeof raw === "string" ? raw : String(raw ?? "");
    return !s.trim() ? "(Empty)" : s;
  }

  const id =
    columnId === "license_type_name"
      ? "license_type"
      : columnId === "property_address"
        ? "address"
        : columnId === "registrant_display_name"
          ? "registrant"
          : columnId;

  const raw =
    id === "display_id"
      ? license.display_id
      : id === "address"
        ? formatPropertyAddressLines(
            license.property?.address1,
            license.property?.address2,
          )
        : id === "license_type"
          ? (license.license_type?.name ?? "")
          : id === "registrant"
            ? formatRegistrantColumnFilterValue(license)
            : id === "status"
              ? getRegistrationStatusDisplay(
                  license.status,
                  license.renewal_date,
                  license.has_awaiting_check_payment,
                  license.cancellation_reason,
                  license.has_awaiting_invoice_payment,
                  license.is_renewable,
                  true,
                  license.total_due,
                ).label
              : id === "needs_attention"
                ? attentionColumnParts(license, false).value
                : id === "amount_due"
                  ? formatRegistrantLicenseAmountDue(license)
                  : id === "total_due"
                    ? formatRegistrantLicenseTotalDue(license)
                    : id === "effective_date"
                      ? license.effective_date
                        ? formatDateNoTimezone(license.effective_date)
                        : ""
                      : id === "renewal_date"
                        ? license.renewal_date
                          ? formatDateNoTimezone(license.renewal_date)
                          : ""
                        : id === "application_date"
                          ? license.created_at
                            ? formatDateTime(license.created_at)
                            : ""
                          : id === "approval_date"
                            ? license.approved_at
                              ? formatDateTime(license.approved_at)
                              : "N/A"
                            : "";
  const s = typeof raw === "string" ? raw : String(raw ?? "");
  return !s.trim() ? "(Empty)" : s;
}

/** Filter licenses by column filters (include row if all column filters include the cell value). */
export function filterLicensesByColumnFilters(
  licenses: RegistrantLicenseListLicenseResponseSchema[],
  columnFilters: Record<string, string[]> | undefined,
): RegistrantLicenseListLicenseResponseSchema[] {
  if (!columnFilters || Object.keys(columnFilters).length === 0)
    return licenses;
  const entries = Object.entries(columnFilters).filter(
    ([_, values]) => Array.isArray(values) && values.length > 0,
  );
  if (entries.length === 0) return licenses;
  return licenses.filter((license) =>
    entries.every(([columnId, values]) =>
      displayValueMatchesFilters(
        getRegistrantLicenseCellValue(license, columnId),
        values,
      ),
    ),
  );
}

/** Compute distinct values per column from licenses (for filter dropdowns). */
export function computeRegistrantLicenseFacetedValues(
  licenses: RegistrantLicenseListLicenseResponseSchema[],
  questionColumns: RegistrantLicenseListQuestionColumnSchema[] = [],
): Record<string, string[]> {
  const questionKeys = listableRegistrantQuestionColumns(questionColumns).map(
    (column) => registrantQuestionColumnAccessorKey(column.key),
  );
  const keys = [...REGISTRANT_LICENSE_COLUMN_KEYS, ...questionKeys];
  const byColumn: Record<string, Map<string, string>> = {};
  for (const key of keys) {
    byColumn[key] = new Map();
  }
  for (const license of licenses) {
    for (const key of keys) {
      const display = getRegistrantLicenseCellValue(license, key);
      byColumn[key].set(
        display.trim().replace(/\s+/g, " ").toLocaleLowerCase(),
        display,
      );
    }
  }
  const out: Record<string, string[]> = {};
  for (const key of keys) {
    out[key] = Array.from(byColumn[key].values()).sort((left, right) =>
      left.localeCompare(right, undefined, { sensitivity: "base" }),
    );
  }
  return out;
}

/** Column definitions for the registrant map filter rail in card layout. */
export function buildRegistrantCardMapColumnProperties(
  facetedValuesByColumn: Record<string, string[]>,
  questionColumns: RegistrantLicenseListQuestionColumnSchema[] = [],
): (
  key: string,
) => ColumnProperties<RegistrantLicenseListLicenseResponseSchema, string> {
  return (accessorKey) => {
    if (accessorKey.startsWith(REGISTRANT_QUESTION_COLUMN_PREFIX)) {
      const columnKey = accessorKey.slice(
        REGISTRANT_QUESTION_COLUMN_PREFIX.length,
      );
      const column = questionColumns.find((item) => item.key === columnKey);
      return {
        displayName: column?.label ?? columnKey,
        defaultVisible: column?.default_visible === true,
        getValue: (row) => getRegistrantLicenseCellValue(row, accessorKey),
        filterValues: facetedValuesByColumn[accessorKey],
        allowBlankFilter: true,
      };
    }
    const columnKey = accessorKey as RegistrantLicenseColumnKey;
    return {
      displayName: getRegistrantLicenseColumnDisplayName(columnKey),
      defaultVisible:
        DEFAULT_REGISTRANT_LICENSE_COLUMN_VISIBILITY[columnKey] ?? true,
      getValue: (row) => getRegistrantLicenseCellValue(row, accessorKey),
      filterValues: facetedValuesByColumn[accessorKey],
      allowBlankFilter: registrantColumnAllowsBlankFilter(accessorKey),
    };
  };
}

function ColumnFilterPopover({
  columnId,
  displayName,
  filterValue,
  facetedValues,
  onFilterChange,
  onClear,
}: {
  columnId: string;
  displayName: string;
  filterValue: string[];
  facetedValues: string[];
  onFilterChange: (columnId: string, values: string[]) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const filteredValues = React.useMemo(() => {
    const selectable = withoutPresenceFilterValues(facetedValues);
    if (!searchValue.trim()) return selectable;
    const q = searchValue.toLowerCase();
    return selectable.filter((v) => v.toLowerCase().includes(q));
  }, [facetedValues, searchValue]);
  const hasActiveFilter = filterValue.length > 0;

  const handleToggle = (value: string) => {
    const next = filterValue.includes(value)
      ? filterValue.filter((v) => v !== value)
      : [...filterValue, value];
    onFilterChange(columnId, next.length > 0 ? next : []);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setSearchValue("");
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          aria-label={`Filter ${displayName}`}
          className={`h-6 w-6 p-0 ${
            hasActiveFilter
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <Filter className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="start"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Command shouldFilter={false}>
          <div className="flex items-center justify-between border-b pl-3.5 pr-2 py-2">
            <span className="text-sm font-medium text-muted-foreground">
              Filter {displayName}
            </span>
            {hasActiveFilter && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  onClear();
                  setOpen(false);
                }}
                className="h-6 w-6 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
          <FilterSearchInput
            placeholder="Search values..."
            value={searchValue}
            onValueChange={setSearchValue}
            focusOnMount
          />
          <p className="border-b px-3 pb-2 text-xs text-muted-foreground">
            Filter suggestions are limited to 500 values.
          </p>
          {registrantColumnAllowsBlankFilter(columnId) ? (
            <ColumnPresenceFilterChoices
              idPrefix={`registrant-filter-${columnId}`}
              values={filterValue}
              onToggle={handleToggle}
            />
          ) : null}
          <CommandList className="max-h-64">
            {searchValue.trim() !== "" ? (
              <CommandGroup>
                <CommandItem
                  className="flex items-center gap-2 text-xs"
                  onMouseDown={(event) => event.preventDefault()}
                  onSelect={() =>
                    handleToggle(encodeContainsFilterValue(searchValue.trim()))
                  }
                >
                  <span className="flex-1">Use “{searchValue.trim()}”</span>
                </CommandItem>
              </CommandGroup>
            ) : null}
            <CommandGroup>
              {filteredValues.length > 0 ? (
                filteredValues.map((value) => (
                  <CommandItem
                    key={value}
                    className="flex items-center gap-2 text-xs"
                    onMouseDown={(event) => event.preventDefault()}
                    onSelect={() => handleToggle(value)}
                  >
                    <Checkbox
                      checked={filterValue.includes(value)}
                      onCheckedChange={() => handleToggle(value)}
                      onClick={(event) => event.stopPropagation()}
                    />
                    <span className="flex-1">{value}</span>
                  </CommandItem>
                ))
              ) : (
                <CommandEmpty>No results found.</CommandEmpty>
              )}
            </CommandGroup>
            {hasActiveFilter && (
              <div className="border-t p-2">
                <Button
                  variant="link"
                  size="xs"
                  onClick={() => {
                    onClear();
                    setOpen(false);
                  }}
                  className="w-full justify-start text-xs p-0 h-auto"
                >
                  Clear filter
                </Button>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface LicenseTableProps {
  licenses: RegistrantLicenseListLicenseResponseSchema[];
  userType: UserType;
  /** When true, show Registrant column (name + company). Defaults on for OMT/AMS. */
  showRegistrantColumn?: boolean;
  /** Which columns to show (one state for all jurisdiction boxes). Undefined = all visible. */
  columnVisibility?: Record<string, boolean>;
  /** Column filters (one state for all jurisdiction boxes). */
  columnFilters?: Record<string, string[]>;
  onColumnFiltersChange?: (filters: Record<string, string[]>) => void;
  /** Distinct values per column for filter dropdowns (from all jurisdictions). */
  facetedValuesByColumn?: Record<string, string[]>;
  /** When set, row click opens this handler instead of navigating to the license view route. */
  onLicenseRowClick?: (licenseId: string) => void;
  /** Optional question-backed columns (hidden unless columnVisibility[qcol:key] is true). */
  questionColumns?: RegistrantLicenseListQuestionColumnSchema[];
  /**
   * When the table sits inside a page list scroller (registrant/OMT dashboard),
   * skip the boxed 70vh pane so rows scroll with the page chrome.
   */
  scrollWithPage?: boolean;
  /** Grey the existing rows while a replacement list page loads. */
  isRefreshing?: boolean;
}

export function LicenseTable({
  licenses,
  userType,
  showRegistrantColumn,
  columnVisibility,
  columnFilters,
  onColumnFiltersChange,
  facetedValuesByColumn,
  onLicenseRowClick,
  questionColumns = [],
  scrollWithPage = false,
  isRefreshing = false,
}: LicenseTableProps) {
  const includeRegistrantColumn =
    showRegistrantColumn ?? userType === UserType.OMT_AMS;
  const listableQuestionColumns = React.useMemo(
    () => listableRegistrantQuestionColumns(questionColumns),
    [questionColumns],
  );
  const col = (key: string, defaultVisible = false) =>
    isColumnVisible(columnVisibility, key, defaultVisible);
  const visibleQuestionColumns = listableQuestionColumns.filter((column) =>
    col(
      registrantQuestionColumnAccessorKey(column.key),
      column.default_visible === true,
    ),
  );
  const navigate = useNavigate();
  const { canMakePayments } = useRegistrantOrgPermissions();

  const activateLicenseRow = React.useCallback(
    (licenseId: string) => {
      if (onLicenseRowClick) {
        onLicenseRowClick(licenseId);
      } else {
        navigate({
          to: getLicenseActionLink(userType, "view", licenseId),
        });
      }
    },
    [navigate, onLicenseRowClick, userType],
  );
  const showFilters =
    !!columnFilters && !!onColumnFiltersChange && !!facetedValuesByColumn;

  const handleFilterChange = React.useCallback(
    (columnId: string, values: string[]) => {
      const next = { ...columnFilters };
      if (values.length > 0) next[columnId] = values;
      else delete next[columnId];
      onColumnFiltersChange?.(next);
    },
    [columnFilters, onColumnFiltersChange],
  );

  const clearFilter = React.useCallback(
    (columnId: string) => {
      const next = { ...columnFilters };
      delete next[columnId];
      onColumnFiltersChange?.(next);
    },
    [columnFilters, onColumnFiltersChange],
  );

  const handlePayClick = (licenseId: string) => {
    navigate({
      to: "/registrant/licenses/$licenseId/payment",
      params: { licenseId },
      // Ignored without the pay grant: that form hides the picker.
      search: { paymentMethod: "card" },
    });
  };

  const actionsColumnWidth = licenseRowActionsColumnWidth(licenses, userType);
  const omtAmsViewOnly = userType === UserType.OMT_AMS;

  return (
    <div
      className={cn(
        "relative w-full min-w-0 rounded-md border",
        // A clipping ancestor would steal sticky headers from the page scroller.
        !scrollWithPage && "overflow-hidden",
      )}
      aria-busy={isRefreshing || undefined}
    >
      <ListLoadingOverlay show={isRefreshing} />
      {/* Table view for large screens — scroll when columns exceed viewport */}
      <div
        data-slot="license-table-desktop-pane"
        className={cn(
          "hidden lg:block w-full min-w-0",
          scrollWithPage
            ? // Horizontal scroll stays on the list. The page scroller clips x
              // so the dashboard background and chrome do not move sideways.
              "overflow-x-auto overscroll-x-contain"
            : "max-h-[min(70vh,720px)] overflow-auto overscroll-x-contain",
        )}
      >
        <table
          className={cn(
            "min-w-full divide-y divide-border",
            scrollWithPage && "w-max",
          )}
        >
          <thead className="[&_th]:sticky [&_th]:-top-px [&_th]:z-20 [&_th]:bg-muted">
            <tr>
              {col("display_id") && (
                <th className="w-[8%] px-4 py-3.5 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1">
                    ID #
                    {showFilters && (
                      <ColumnFilterPopover
                        columnId="display_id"
                        displayName="ID #"
                        filterValue={columnFilters?.display_id ?? []}
                        facetedValues={facetedValuesByColumn?.display_id ?? []}
                        onFilterChange={handleFilterChange}
                        onClear={() => clearFilter("display_id")}
                      />
                    )}
                  </div>
                </th>
              )}
              {col("address") && (
                <th className="w-[14%] px-4 py-3.5 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1">
                    Address
                    {showFilters && (
                      <ColumnFilterPopover
                        columnId="address"
                        displayName="Address"
                        filterValue={columnFilters?.address ?? []}
                        facetedValues={facetedValuesByColumn?.address ?? []}
                        onFilterChange={handleFilterChange}
                        onClear={() => clearFilter("address")}
                      />
                    )}
                  </div>
                </th>
              )}
              {col("property_apn") && (
                <th className="w-[10%] px-4 py-3.5 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1">
                    {getRegistrantLicenseColumnDisplayName("property_apn")}
                    {showFilters && (
                      <ColumnFilterPopover
                        columnId="property_apn"
                        displayName={getRegistrantLicenseColumnDisplayName(
                          "property_apn",
                        )}
                        filterValue={columnFilters?.property_apn ?? []}
                        facetedValues={
                          facetedValuesByColumn?.property_apn ?? []
                        }
                        onFilterChange={handleFilterChange}
                        onClear={() => clearFilter("property_apn")}
                      />
                    )}
                  </div>
                </th>
              )}
              {col("county") && (
                <th className="w-[8%] px-4 py-3.5 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1">
                    County
                    {showFilters && (
                      <ColumnFilterPopover
                        columnId="county"
                        displayName="County"
                        filterValue={columnFilters?.county ?? []}
                        facetedValues={facetedValuesByColumn?.county ?? []}
                        onFilterChange={handleFilterChange}
                        onClear={() => clearFilter("county")}
                      />
                    )}
                  </div>
                </th>
              )}
              {col("state") && (
                <th className="w-[6%] px-4 py-3.5 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1">
                    State
                    {showFilters && (
                      <ColumnFilterPopover
                        columnId="state"
                        displayName="State"
                        filterValue={columnFilters?.state ?? []}
                        facetedValues={facetedValuesByColumn?.state ?? []}
                        onFilterChange={handleFilterChange}
                        onClear={() => clearFilter("state")}
                      />
                    )}
                  </div>
                </th>
              )}
              {col("license_type") && (
                <th className="w-[12%] px-4 py-3.5 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1">
                    License Type
                    {showFilters && (
                      <ColumnFilterPopover
                        columnId="license_type"
                        displayName="License Type"
                        filterValue={columnFilters?.license_type ?? []}
                        facetedValues={
                          facetedValuesByColumn?.license_type ?? []
                        }
                        onFilterChange={handleFilterChange}
                        onClear={() => clearFilter("license_type")}
                      />
                    )}
                  </div>
                </th>
              )}
              {col("registrant") && includeRegistrantColumn && (
                <th className="w-[12%] px-4 py-3.5 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1">
                    Registrant
                    {showFilters && (
                      <ColumnFilterPopover
                        columnId="registrant"
                        displayName="Registrant"
                        filterValue={columnFilters?.registrant ?? []}
                        facetedValues={facetedValuesByColumn?.registrant ?? []}
                        onFilterChange={handleFilterChange}
                        onClear={() => clearFilter("registrant")}
                      />
                    )}
                  </div>
                </th>
              )}
              {col("status") && (
                <th className="w-[12%] px-4 py-3.5 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1">
                    Status
                    {showFilters && (
                      <ColumnFilterPopover
                        columnId="status"
                        displayName="Status"
                        filterValue={columnFilters?.status ?? []}
                        facetedValues={facetedValuesByColumn?.status ?? []}
                        onFilterChange={handleFilterChange}
                        onClear={() => clearFilter("status")}
                      />
                    )}
                  </div>
                </th>
              )}
              {col("needs_attention") && (
                <th className="w-[12%] px-4 py-3.5 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1">
                    Needs Attention
                    {showFilters && (
                      <ColumnFilterPopover
                        columnId="needs_attention"
                        displayName="Needs Attention"
                        filterValue={columnFilters?.needs_attention ?? []}
                        facetedValues={
                          facetedValuesByColumn?.needs_attention ?? []
                        }
                        onFilterChange={handleFilterChange}
                        onClear={() => clearFilter("needs_attention")}
                      />
                    )}
                  </div>
                </th>
              )}
              {col("amount_due") && (
                <th className="w-[10%] px-4 py-3.5 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1">
                    {getRegistrantLicenseColumnDisplayName("amount_due")}
                    {showFilters && (
                      <ColumnFilterPopover
                        columnId="amount_due"
                        displayName={getRegistrantLicenseColumnDisplayName(
                          "amount_due",
                        )}
                        filterValue={columnFilters?.amount_due ?? []}
                        facetedValues={facetedValuesByColumn?.amount_due ?? []}
                        onFilterChange={handleFilterChange}
                        onClear={() => clearFilter("amount_due")}
                      />
                    )}
                  </div>
                </th>
              )}
              {col("total_due") && (
                <th className="w-[10%] px-4 py-3.5 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1">
                    {getRegistrantLicenseColumnDisplayName("total_due")}
                    {showFilters && (
                      <ColumnFilterPopover
                        columnId="total_due"
                        displayName={getRegistrantLicenseColumnDisplayName(
                          "total_due",
                        )}
                        filterValue={columnFilters?.total_due ?? []}
                        facetedValues={facetedValuesByColumn?.total_due ?? []}
                        onFilterChange={handleFilterChange}
                        onClear={() => clearFilter("total_due")}
                      />
                    )}
                  </div>
                </th>
              )}
              {col("effective_date") && (
                <th className="w-[10%] px-4 py-3.5 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1">
                    {getRegistrantLicenseColumnDisplayName("effective_date")}
                    {showFilters && (
                      <ColumnFilterPopover
                        columnId="effective_date"
                        displayName={getRegistrantLicenseColumnDisplayName(
                          "effective_date",
                        )}
                        filterValue={columnFilters?.effective_date ?? []}
                        facetedValues={
                          facetedValuesByColumn?.effective_date ?? []
                        }
                        onFilterChange={handleFilterChange}
                        onClear={() => clearFilter("effective_date")}
                      />
                    )}
                  </div>
                </th>
              )}
              {col("renewal_date") && (
                <th className="w-[10%] px-4 py-3.5 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1">
                    Renewal Date
                    {showFilters && (
                      <ColumnFilterPopover
                        columnId="renewal_date"
                        displayName="Renewal Date"
                        filterValue={columnFilters?.renewal_date ?? []}
                        facetedValues={
                          facetedValuesByColumn?.renewal_date ?? []
                        }
                        onFilterChange={handleFilterChange}
                        onClear={() => clearFilter("renewal_date")}
                      />
                    )}
                  </div>
                </th>
              )}
              {col("application_date") && (
                <th className="w-[10%] px-4 py-3.5 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1">
                    Application Date
                    {showFilters && (
                      <ColumnFilterPopover
                        columnId="application_date"
                        displayName="Application Date"
                        filterValue={columnFilters?.application_date ?? []}
                        facetedValues={
                          facetedValuesByColumn?.application_date ?? []
                        }
                        onFilterChange={handleFilterChange}
                        onClear={() => clearFilter("application_date")}
                      />
                    )}
                  </div>
                </th>
              )}
              {col("approval_date") && (
                <th className="w-[10%] px-4 py-3.5 text-left text-sm font-semibold">
                  <div className="flex items-center gap-1">
                    Approval Date
                    {showFilters && (
                      <ColumnFilterPopover
                        columnId="approval_date"
                        displayName="Approval Date"
                        filterValue={columnFilters?.approval_date ?? []}
                        facetedValues={
                          facetedValuesByColumn?.approval_date ?? []
                        }
                        onFilterChange={handleFilterChange}
                        onClear={() => clearFilter("approval_date")}
                      />
                    )}
                  </div>
                </th>
              )}
              {visibleQuestionColumns.map((column) => {
                const accessorKey = registrantQuestionColumnAccessorKey(
                  column.key,
                );
                return (
                  <th
                    key={accessorKey}
                    className="w-[12%] px-4 py-3.5 text-left text-sm font-semibold"
                  >
                    <div className="flex items-center gap-1">
                      {column.label}
                      {showFilters && (
                        <ColumnFilterPopover
                          columnId={accessorKey}
                          displayName={column.label}
                          filterValue={columnFilters?.[accessorKey] ?? []}
                          facetedValues={
                            facetedValuesByColumn?.[accessorKey] ?? []
                          }
                          onFilterChange={handleFilterChange}
                          onClear={() => clearFilter(accessorKey)}
                        />
                      )}
                    </div>
                  </th>
                );
              })}
              {!omtAmsViewOnly && (
                <th
                  className="sticky right-0 z-30 px-1 py-3.5 text-left text-sm font-semibold bg-muted shadow-[-1px_0_0_0_hsl(var(--border))]"
                  style={{
                    width: actionsColumnWidth,
                    minWidth: actionsColumnWidth,
                    maxWidth: actionsColumnWidth,
                  }}
                >
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {licenses.map((license) => {
              const registrationDisplay = getRegistrationStatusDisplay(
                license.status,
                license.renewal_date,
                license.has_awaiting_check_payment,
                license.cancellation_reason,
                license.has_awaiting_invoice_payment,
                license.is_renewable,
                true,
                license.total_due,
              );

              // Get inspection status if available
              const inspectionDisplay = license.newest_inspection_status
                ? getInspectionStatusDisplay(
                    license.newest_inspection_status,
                    license.newest_inspection_has_awaiting_check_payment,
                    license.status !== "PAID_PENDING_REVIEW",
                    license.newest_inspection_has_awaiting_invoice_payment,
                  )
                : null;

              const showInspectionStatus =
                inspectionDisplay != null &&
                shouldShowInspectionStatusOnLicenseList(
                  license.status,
                  registrationDisplay.label,
                );
              const showLicenseStatus = !showInspectionStatus;

              return (
                <tr
                  key={license.id}
                  // biome-ignore lint/a11y/useSemanticElements: table row must be tr for layout
                  role="button"
                  tabIndex={0}
                  className={cn(
                    "group cursor-pointer hover:bg-muted/50",
                    license.needs_attention && "bg-warning/5",
                  )}
                  onClick={() => activateLicenseRow(license.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      activateLicenseRow(license.id);
                    }
                  }}
                >
                  {col("display_id") && (
                    <td className="text-ellipsis whitespace-nowrap px-4 py-4 text-sm">
                      {license.display_id}
                    </td>
                  )}
                  {col("address") && (
                    <td className="text-ellipsis whitespace-nowrap px-4 py-4 text-sm">
                      {license.property
                        ? formatPropertyAddressLines(
                            license.property.address1,
                            license.property.address2,
                          ) || "No address"
                        : "No address"}
                    </td>
                  )}
                  {col("property_apn") && (
                    <td className="text-ellipsis whitespace-nowrap px-4 py-4 text-sm">
                      {license.property?.apn?.trim() || ""}
                    </td>
                  )}
                  {col("county") && (
                    <td className="text-ellipsis whitespace-nowrap px-4 py-4 text-sm">
                      {license.jurisdiction?.county?.trim() || "—"}
                    </td>
                  )}
                  {col("state") && (
                    <td className="text-ellipsis whitespace-nowrap px-4 py-4 text-sm">
                      {license.jurisdiction?.state?.trim() || "—"}
                    </td>
                  )}
                  {col("license_type") && (
                    <td className="text-ellipsis whitespace-nowrap px-4 py-4 text-sm">
                      {license.license_type?.name || ""}
                    </td>
                  )}
                  {col("registrant") && includeRegistrantColumn && (
                    <td className="px-4 py-4 text-sm">
                      <RegistrantColumnLines license={license} />
                    </td>
                  )}
                  {col("status") && (
                    <td className="whitespace-nowrap px-4 py-4 text-sm">
                      <div className="flex flex-col gap-1">
                        <div className="flex gap-2 flex-wrap">
                          {showLicenseStatus && (
                            <Badge variant={registrationDisplay.variant}>
                              {registrationDisplay.label}
                            </Badge>
                          )}
                          {showInspectionStatus && (
                            <Badge variant={inspectionDisplay.variant}>
                              {inspectionDisplay.label}
                            </Badge>
                          )}
                        </div>
                        {license.status === "CANCELLED" &&
                          license.cancellation_reason &&
                          !isDeregisteredCancellationReason(
                            license.cancellation_reason,
                          ) && (
                            <span className="text-muted-foreground text-xs">
                              {license.cancellation_reason}
                            </span>
                          )}
                      </div>
                    </td>
                  )}
                  {col("needs_attention") && (
                    <td className="whitespace-nowrap px-4 py-4 text-sm">
                      <AttentionBadges row={license} />
                    </td>
                  )}
                  {col("amount_due") && (
                    <td className="whitespace-nowrap px-4 py-4 text-sm tabular-nums">
                      {formatRegistrantLicenseAmountDue(license)}
                    </td>
                  )}
                  {col("total_due") && (
                    <td className="whitespace-nowrap px-4 py-4 text-sm tabular-nums">
                      {formatRegistrantLicenseTotalDue(license)}
                    </td>
                  )}
                  {col("effective_date") && (
                    <td className="text-ellipsis px-4 py-4 text-sm">
                      <div className="whitespace-nowrap">
                        {license.effective_date
                          ? formatDateNoTimezone(license.effective_date)
                          : ""}
                      </div>
                      {licenseShowsServiceTransferDate(license) ? (
                        <div className="whitespace-nowrap text-muted-foreground">
                          Service transfer:{" "}
                          {formatDateNoTimezone(license.transfer_in_date)}
                        </div>
                      ) : null}
                    </td>
                  )}
                  {col("renewal_date") && (
                    <td className="text-ellipsis whitespace-nowrap px-4 py-4 text-sm">
                      {license.renewal_date
                        ? formatDateNoTimezone(license.renewal_date)
                        : ""}
                    </td>
                  )}
                  {col("application_date") && (
                    <td className="text-ellipsis whitespace-nowrap px-4 py-4 text-sm">
                      {license.created_at
                        ? formatDateTime(license.created_at)
                        : ""}
                    </td>
                  )}
                  {col("approval_date") && (
                    <td className="text-ellipsis whitespace-nowrap px-4 py-4 text-sm">
                      {license.approved_at
                        ? formatDateTime(license.approved_at)
                        : "N/A"}
                    </td>
                  )}
                  {visibleQuestionColumns.map((column) => (
                    <td
                      key={registrantQuestionColumnAccessorKey(column.key)}
                      className="text-ellipsis whitespace-nowrap px-4 py-4 text-sm"
                    >
                      {license.question_column_values?.[column.key]?.trim() ||
                        "—"}
                    </td>
                  ))}
                  {!omtAmsViewOnly && (
                    <td
                      className="sticky right-0 z-20 px-1 py-4 text-sm bg-card group-hover:bg-muted/50 shadow-[-1px_0_0_0_hsl(var(--border))]"
                      style={{
                        width: actionsColumnWidth,
                        minWidth: actionsColumnWidth,
                        maxWidth: actionsColumnWidth,
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <LicenseRowActions
                        license={license}
                        userType={userType}
                        canMakePayments={canMakePayments}
                        onPayClick={handlePayClick}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Card view for medium screens */}
      <div className="lg:hidden">
        <div className="space-y-4">
          {licenses.map((license) => {
            const isRejectedLicense = isRejected(license);
            const showRenewButton = isRenewable(license) && !isRejectedLicense;
            const showDeregisterButton = isDeregisterable(license);
            const showPerformInspection = canPerformInspection(license);
            const registrationDisplay = getRegistrationStatusDisplay(
              license.status,
              license.renewal_date,
              license.has_awaiting_check_payment,
              license.cancellation_reason,
              license.has_awaiting_invoice_payment,
              license.is_renewable,
              true,
              license.total_due,
            );

            // Get inspection status if available
            const inspectionDisplay = license.newest_inspection_status
              ? getInspectionStatusDisplay(
                  license.newest_inspection_status,
                  license.newest_inspection_has_awaiting_check_payment,
                  license.status !== "PAID_PENDING_REVIEW",
                  license.newest_inspection_has_awaiting_invoice_payment,
                )
              : null;

            const showInspectionStatus =
              inspectionDisplay != null &&
              shouldShowInspectionStatusOnLicenseList(
                license.status,
                registrationDisplay.label,
              );
            const showLicenseStatus = !showInspectionStatus;

            return (
              <div
                key={license.id}
                // biome-ignore lint/a11y/useSemanticElements: card layout uses div for styling
                role="button"
                tabIndex={0}
                className="p-4 sm:p-4 p-2 cursor-pointer hover:bg-muted/50 rounded-md"
                onClick={() => activateLicenseRow(license.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    activateLicenseRow(license.id);
                  }
                }}
              >
                <div className="flex flex-col space-y-2">
                  {col("address") && (
                    <div className="flex items-center justify-between">
                      <span className="font-medium">
                        {" "}
                        {license.property
                          ? formatPropertyAddressLines(
                              license.property.address1,
                              license.property.address2,
                            ) || "No address provided"
                          : "No address provided"}
                      </span>
                      {col("status") && (
                        <div className="flex gap-2 flex-wrap">
                          {showLicenseStatus && (
                            <Badge variant={registrationDisplay.variant}>
                              {registrationDisplay.label}
                            </Badge>
                          )}
                          {showInspectionStatus && (
                            <Badge variant={inspectionDisplay.variant}>
                              {inspectionDisplay.label}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {!col("address") && col("status") && (
                    <div className="flex gap-2 flex-wrap">
                      {showLicenseStatus && (
                        <Badge variant={registrationDisplay.variant}>
                          {registrationDisplay.label}
                        </Badge>
                      )}
                      {showInspectionStatus && (
                        <Badge variant={inspectionDisplay.variant}>
                          {inspectionDisplay.label}
                        </Badge>
                      )}
                    </div>
                  )}
                  {col("property_apn") && (
                    <div className="text-sm">
                      {getRegistrantLicenseColumnDisplayName("property_apn")}:{" "}
                      {license.property?.apn?.trim() || "—"}
                    </div>
                  )}
                  {col("display_id") && (
                    <div className="text-sm">ID #: {license.display_id}</div>
                  )}
                  {col("county") && (
                    <div className="text-sm">
                      County: {license.jurisdiction?.county?.trim() || "—"}
                    </div>
                  )}
                  {col("state") && (
                    <div className="text-sm">
                      State: {license.jurisdiction?.state?.trim() || "—"}
                    </div>
                  )}
                  {col("license_type") && license.license_type?.name && (
                    <div className="text-sm">
                      License Type: {license.license_type.name}
                    </div>
                  )}
                  {col("registrant") && includeRegistrantColumn && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">
                        Registrant:{" "}
                      </span>
                      <RegistrantColumnLines license={license} />
                    </div>
                  )}
                  {col("needs_attention") &&
                    attentionColumnParts(license, false).labels.length > 0 && (
                      <div className="text-sm">
                        <AttentionBadges row={license} />
                      </div>
                    )}
                  {col("amount_due") && (
                    <div className="text-sm tabular-nums">
                      {getRegistrantLicenseColumnDisplayName("amount_due")}:{" "}
                      {formatRegistrantLicenseAmountDue(license)}
                    </div>
                  )}
                  {col("total_due") && (
                    <div className="text-sm tabular-nums">
                      {getRegistrantLicenseColumnDisplayName("total_due")}:{" "}
                      {formatRegistrantLicenseTotalDue(license)}
                    </div>
                  )}
                  {col("effective_date") && license.effective_date && (
                    <div className="text-sm">
                      {getRegistrantLicenseColumnDisplayName("effective_date")}:{" "}
                      {formatDateNoTimezone(license.effective_date)}
                    </div>
                  )}
                  {col("effective_date") &&
                    licenseShowsServiceTransferDate(license) && (
                      <div className="text-sm">
                        Service Transfer Date:{" "}
                        {formatDateNoTimezone(license.transfer_in_date)}
                      </div>
                    )}
                  {col("renewal_date") && license.renewal_date && (
                    <div className="text-sm">
                      Renewal Date:{" "}
                      {license.renewal_date
                        ? formatDateNoTimezone(license.renewal_date)
                        : ""}
                    </div>
                  )}
                  {col("application_date") && license.created_at && (
                    <div className="text-sm">
                      Application Date: {formatDateTime(license.created_at)}
                    </div>
                  )}
                  {col("approval_date") && (
                    <div className="text-sm">
                      Approval Date:{" "}
                      {license.approved_at
                        ? formatDateTime(license.approved_at)
                        : "N/A"}
                    </div>
                  )}
                  {visibleQuestionColumns.map((column) => (
                    <div
                      key={registrantQuestionColumnAccessorKey(column.key)}
                      className="text-sm"
                    >
                      {column.label}:{" "}
                      {license.question_column_values?.[column.key]?.trim() ||
                        "—"}
                    </div>
                  ))}
                  {license.status === "CANCELLED" &&
                    license.cancellation_reason &&
                    !isDeregisteredCancellationReason(
                      license.cancellation_reason,
                    ) && (
                      <div className="text-sm text-muted-foreground">
                        Cancellation reason: {license.cancellation_reason}
                      </div>
                    )}
                  <div
                    className="flex flex-wrap justify-end gap-2"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    {!omtAmsViewOnly && showPerformInspection && (
                      <PerformInspectionButton
                        licenseId={license.id}
                        className="flex-1 sm:flex-none min-w-[80px]"
                      />
                    )}
                    {!omtAmsViewOnly &&
                      isPayable(license) &&
                      canMakePayments && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                          onClick={() => handlePayClick(license.id)}
                        >
                          <DollarSign className="h-4 w-4" />
                          Pay Now
                        </Button>
                      )}
                    {!omtAmsViewOnly && showRenewButton && (
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="flex-1 sm:flex-none min-w-[80px]"
                      >
                        <Link
                          to={getLicenseActionLink(
                            userType,
                            "renew",
                            license.id,
                          )}
                        >
                          <RefreshCw className="h-4 w-4" />
                          <span className="ml-1">Renew</span>
                        </Link>
                      </Button>
                    )}
                    {!omtAmsViewOnly && isRejectedLicense && (
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="flex-1 sm:flex-none min-w-[80px]"
                      >
                        <Link
                          to={getLicenseActionLink(
                            userType,
                            "renew",
                            license.id,
                          )}
                        >
                          <RotateCcw className="h-4 w-4" />
                          <span className="ml-1">Reapply</span>
                        </Link>
                      </Button>
                    )}
                    {!omtAmsViewOnly && showDeregisterButton && (
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="flex-1 sm:flex-none min-w-[80px]"
                      >
                        <Link
                          to={getLicenseActionLink(
                            userType,
                            "deregister",
                            license.id,
                          )}
                        >
                          <LogOut className="h-4 w-4" />
                          <span className="ml-1">De-register</span>
                        </Link>
                      </Button>
                    )}
                    {!omtAmsViewOnly &&
                      isCancelableForRegistrant(license.status) && (
                        <Button
                          variant="outline"
                          size="sm"
                          asChild
                          className="flex-1 sm:flex-none min-w-[80px] text-destructive hover:bg-destructive hover:text-destructive-foreground"
                        >
                          <Link
                            to={getLicenseActionLink(
                              userType,
                              "cancel",
                              license.id,
                            )}
                          >
                            <Trash2 className="h-4 w-4" />
                            Cancel
                          </Link>
                        </Button>
                      )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
